import { classNames } from '../utils/UNSAFE_classNames';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { ComponentChildren, JSX } from 'preact';
import { Flex } from '../UNSAFE_Flex';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { sizeToCSS, Size } from '../utils/UNSAFE_size';

const labelSlotStyles = {
  base: cssProps`${{
    display: 'block',
    flexGrow: '0',
    flexShrink: '1',
    boxSizing: 'border-box'
  }}`,
  start: cssProps`${{
    paddingInlineEnd: 'var(--oj-c-PRIVATE-DO-NOT-USE-form-layout-start-edge-label-to-value-padding)'
  }}`,

  top: cssProps`${{
    flexBasis: '100%',
    width: '100%',
    maxWidth: '100%',
    paddingBlockEnd: 'var(--oj-c-PRIVATE-DO-NOT-USE-form-layout-top-edge-label-to-value-padding)'
  }}`,

  topPureReadonly: cssProps`${{
    paddingBlockEnd: 0
  }}`
};

const labelInnerStyles = {
  // base is labelEdge 'top'
  base: cssProps`${{
    display: 'block',
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-color)',
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-font-size)',
    lineHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-line-height)',
    width: '100%'
  }}`,
  start: cssProps`${{
    display: 'inline-flex',
    alignItems: 'center'
  }}`,
  startNotPureReadonly: cssProps`${{
    minHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-height)'
  }}`,
  // TODO: Use Text Component instead of CSS
  noWrap: cssProps`${{
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
    overflowY: 'hidden',
    textOverflow: 'ellipsis'
  }}`
};

const valueSlotStyles = cssProps`${{
  flex: '1 1 100%',
  width: '100%',
  maxWidth: '100%'
}}`;

type Props = {
  /**
   * Label content of the layout
   */
  label?: ComponentChildren;
  /**
   * Defines how the label is going to be positioned
   */
  labelEdge?: 'start' | 'top';
  /**
   * Defines the label width for labelEdge 'start' ('top' is always 100%)
   */
  labelStartWidth?: Size;
  /**
   * Value content of the layout
   */
  children: ComponentChildren;
};

export const LabelValueLayout = ({
  label,
  labelEdge,
  children,
  labelStartWidth = '33%'
}: Props) => {
  const width = sizeToCSS(labelStartWidth);
  const valueWidth = `calc(100% - ${width})`;
  const labelStyles: JSX.CSSProperties =
    labelEdge === 'start' ? { flexBasis: width, width: width, maxWidth: width } : {};
  const valueStyles: JSX.CSSProperties =
    labelEdge === 'start' ? { flexBasis: valueWidth, width: valueWidth, maxWidth: valueWidth } : {};

  const { isFormLayout, isReadonly: isFormReadonly, labelWrapping } = useFormContext();
  const { isReadonly } = useFormFieldContext();
  const isPureReadonly = (isFormLayout && isFormReadonly) || (!isFormLayout && isReadonly);
  const labelStyleClasses = classNames([
    labelSlotStyles.base,
    labelEdge === 'start' && labelSlotStyles.start,
    // labelEdge === 'start' && isPureReadonly && labelSlotStyles.startReadonly,
    // labelEdge === 'start' && !isPureReadonly && labelSlotStyles.startNotPureReadonly,
    labelEdge === 'top' && labelSlotStyles.top,
    labelEdge === 'top' && isPureReadonly && labelSlotStyles.topPureReadonly
  ]);
  const labelInnerClasses = classNames([
    labelInnerStyles.base,
    labelEdge === 'start' && labelInnerStyles.start,
    labelEdge === 'start' && !isPureReadonly && labelInnerStyles.startNotPureReadonly,
    labelWrapping === 'truncate' && labelInnerStyles.noWrap
  ]);

  return (
    <Flex wrap="wrap" align="start">
      <div class={labelStyleClasses} style={labelStyles}>
        <div class={labelInnerClasses}>{label}</div>
      </div>
      <div class={valueSlotStyles} style={valueStyles}>
        {children}
      </div>
    </Flex>
  );
};
